import * as ReactIs from 'react-is';
import * as IDB from "idb-keyval";
import {UIUtilsEventTypes,} from "./type-declerations";
import type {SxProps, SystemStyleObject} from '@mui/system';
import EventEmitter from "eventemitter3";
// @ts-ignore
import {ReactComponent as FolderIcon} from '../../assets/images/file/folder.svg';
// @ts-ignore
import {ReactComponent as ImageIcon} from '../../assets/images/file/image.svg';
// @ts-ignore
import {ReactComponent as PdfIcon} from '../../assets/images/file/pdf.svg';
// @ts-ignore
import {ReactComponent as TextIcon} from '../../assets/images/file/text.svg';
// @ts-ignore
import {ReactComponent as VideoIcon} from '../../assets/images/file/video.svg';
// @ts-ignore
import {ReactComponent as ZipIcon} from '../../assets/images/file/zip.svg';
// @ts-ignore
import {ReactComponent as Machinery} from '../../assets/images/industry/machinery.svg';
import {ReactElement} from "react";
import {BedroomBabyRounded, DirectionsCarRounded, LocalShippingRounded, SettingsRemoteRounded} from "@mui/icons-material";
import Texts from '../../core/models/static/texts/texts';
import {ApiRequestDs, ApiResponseDs} from '../../@bizkeytech/base/core';
import Utils from '../../core/services/utils/utils';
import EnvService from '../../core/services/env';
import {InputFileAcceptTypes, PlatformIndustryIds} from "../../core/constants/enums";


/**
 * Utilities specific for the UI of the project.
 */
class UIUtils extends EventEmitter<UIUtilsEventTypes> {

    // The prompt for the PWA installation.
    private static PWAInstallationPrompt: BeforeInstallPromptEvent | undefined = undefined;
    // The match media for the PWA.
    private static PWAMatch: MediaQueryList = window.matchMedia('(display-mode: standalone)');

    /**
     * The instance of the UIUtils.
     */
    public static readonly emitter = new UIUtils();


    /**
     * Determines if the PWA can be installed.
     * @return     Whether the PWA can be installed.
     */
    public static get PWAInstallable(): boolean {
        return !!this.PWAInstallationPrompt;
    }

    /**
     * Determines if the PWA is running.
     * @return     Whether the PWA is running.
     */
    public static get PWARunning(): boolean {
        return this.PWAMatch.matches;
    }

    /**
     * The prefix of the theme options.
     */
    public static readonly ThemePrefix = {
        default: '',
        previewContent: 'bizkey-preview-content',
    };

    /**
     * The prefix of the theme options with the --. Specific for the variable names.
     * @param themePrefix   the prefix of the theme.
     * @return              the prefix with --.
     */
    public static withThemePrefix(themePrefix = this.ThemePrefix.default) {
        return '--'.concat(!themePrefix.length ? '' : themePrefix.concat('-'))
    }

    /**
     * The ids of the toast containers.
     */
    public static readonly ToastContainerIDs = {
        default: 'default',
        api: 'api',
    }

    /**
     * Creates a css variable with the theme-prefix.
     * @param name      the name of the variable.
     * @param themePrefix   the prefix of the theme.
     */
    public static themeVar(name: string, themePrefix = this.ThemePrefix.default) {
        return `var(${this.withThemePrefix(themePrefix)}${name})`;
    };

    /**
     * Invokes the given sx function with the given theme.
     * @param sx        the sx function to be invoked
     * @param theme     the theme to be passed to the sx function
     */
    public static invokeSx<Theme extends object>(sx?: SxProps<Theme>, theme?: Theme): SystemStyleObject<Theme> | ReadonlyArray<boolean | SystemStyleObject<Theme>> {
        if (!sx) {
            return {};
        }
        if (typeof sx === 'function') {
            if (!theme) {
                throw new Error('Theme must be provided when invoking a sx function.');
            }
            return sx(theme);
        }
        if (Array.isArray(sx)) {
            return sx.map(s => this.invokeSx(s, theme)) as ReadonlyArray<boolean | SystemStyleObject<Theme>>;
        }
        return sx as SystemStyleObject<Theme>;
    }

    /**
     * Determines if the given object is a valid element for the React to render.
     * @param obj
     */
    public static isObjectValidElementType(obj: any): boolean {
        if (obj === null)
            return false;
        return ReactIs.isValidElementType(obj);
    }

    /**
     * Determines if the given object is a valid element for the React to render.
     * @param obj
     */
    public static isObjectValidElement(obj: any): boolean {
        if (obj === null)
            return false;
        return ReactIs.isElement(obj);
    }

    /**
     * Captures the PWA installation prompt.
     */
    public static capturePWAInstallationPrompt() {
        window.addEventListener('beforeinstallprompt', async (e) => {
            this.PWAInstallationPrompt = e as any;
            await IDB.set('pwa-installed', false);
            this.emitter.emit('pwa-installable', true);
        });
        window.addEventListener('appinstalled', async () => {
            this.PWAInstallationPrompt = undefined;
            await IDB.set('pwa-installed', true);
            this.emitter.emit('pwa-installable', false);
        });
    }

    /**
     * Listens to the event of the PWA installation prompt.
     * @param callback    the callback to be invoked when the event is emitted.
     * @return            the function to be invoked to remove the listener.
     */
    public static onPWAInstallable(callback: EventEmitter.EventListener<UIUtilsEventTypes, 'pwa-installable'>) {
        this.emitter.on('pwa-installable', callback);
        this.emitter.emit('pwa-installable', this.PWAInstallable);
        return () => {
            this.emitter.off('pwa-installable', callback)
        };
    }

    /**
     * Listens to the event of the PWA running.
     * @param callback      the callback to be invoked when the event is emitted.
     */
    public static onPWARunning(callback: EventEmitter.EventListener<UIUtilsEventTypes, 'pwa-running'>) {
        const listener: (ev: MediaQueryListEvent) => any = (evt) => {
            let displayMode = 'browser';
            if (evt.matches) {
                displayMode = 'standalone';
            }
            this.emitter.emit('pwa-running', displayMode === 'standalone');
        }
        this.PWAMatch.addEventListener('change', listener);
        this.emitter.on('pwa-running', callback);
        this.emitter.emit('pwa-running', this.PWARunning);
        return () => {
            this.PWAMatch.removeEventListener('change', listener);
            this.emitter.off('pwa-running', callback);
        }
    }

    /**
     * Prompts the user to install the PWA.
     */
    public static async promptPWAInstallation() {
        if (!!this.PWAInstallationPrompt) {
            const choice = await this.PWAInstallationPrompt.prompt();
            if (choice.outcome === 'accepted') {
                this.PWAInstallationPrompt = undefined;
                await IDB.set('pwa-installed', true);
                this.emitter.emit('pwa-installable', false);
            }
            this.PWAInstallationPrompt?.userChoice.then((choice) => {
                if (choice.outcome === 'accepted') {
                    this.PWAInstallationPrompt = undefined;
                    IDB.set('pwa-installed', true);
                    this.emitter.emit('pwa-installable', false);
                }
            })
        } else {
            import('../../core/services/toast').then(({default: ToastService}) => {
                ToastService.error({
                    title: 'PWA Installation',
                    message: `PWA installation prompt is not available. Please ensure you are using the latest version of Google Chrome as your 
                Browser as this feature is has limited support on other browsers.`,
                })
            });
        }
    }

    /**
     * Determines if the given file is an image.
     * @param extension
     */
    static isImage(extension: string | undefined): boolean {
        if (!extension) {
            return false;
        }
        return [
            InputFileAcceptTypes.png,
            InputFileAcceptTypes.jpg,
            InputFileAcceptTypes.jpeg,
            InputFileAcceptTypes.gif,
            InputFileAcceptTypes.bmp,
            InputFileAcceptTypes.tiff,
            InputFileAcceptTypes.apng,
            InputFileAcceptTypes.pjpeg,
            InputFileAcceptTypes.tif,
            InputFileAcceptTypes.svg,
            InputFileAcceptTypes.webp,
            InputFileAcceptTypes.heic,
            InputFileAcceptTypes.heif,
            InputFileAcceptTypes.ico,
            InputFileAcceptTypes.avif,
            // remove the . from the start of the extension.
        ].map(e => e.replace('.', '')).includes(extension.replace('.', ''));
    }

    /**
     * Determines if a file is a video.
     */
    static isVideo(extension: string | undefined): boolean {
        if (!extension) {
            return false;
        }
        return [
            InputFileAcceptTypes.mp4,
            InputFileAcceptTypes.avi,
            InputFileAcceptTypes.mov,
            InputFileAcceptTypes.webm,
            InputFileAcceptTypes.mkv,
            InputFileAcceptTypes.hevc,
            InputFileAcceptTypes.flv,
            InputFileAcceptTypes.wmv,
            InputFileAcceptTypes.mpg
            // remove the . from the start of the extension.
        ].map(e => e.replace('.', '')).includes(extension.replace('.', ''));
    }

    /**
     * Returns the icon for the given file type.
     */
    static getIndustryIcon(industryFileName: string, industryId?: string, returnNullIfNotFound = false) {
        if (!industryFileName) {
            switch (industryId?.toUpperCase()) {
                case PlatformIndustryIds.hdAutoPart.toUpperCase():
                    return <LocalShippingRounded/>;
                case PlatformIndustryIds.autoPart.toUpperCase():
                    return <DirectionsCarRounded/>;
                case PlatformIndustryIds.machinery.toUpperCase():
                    return <Machinery/>;
                case PlatformIndustryIds.westernApparel.toUpperCase():
                    return <BedroomBabyRounded/>;
                case PlatformIndustryIds.boilerControls.toUpperCase():
                    return <SettingsRemoteRounded/>;
                default:
                    return returnNullIfNotFound ? null : <></>;
            }
        }
        switch (industryFileName) {
            case 'hdpart.svg':
                return <LocalShippingRounded/>;
            case 'autopart.svg':
                return <DirectionsCarRounded/>;
            case 'machinery.svg':
                return <Machinery/>;
            case 'western.svg':
                return <BedroomBabyRounded/>;
            case "controls.svg":
                return <SettingsRemoteRounded/>;
            default:
                return returnNullIfNotFound ? null : <></>;
        }
    }

    /**
     * Returns the icon for the given file type.
     */
    static getFileIcon(extension?: string, isFolder?: boolean, placeholder: ReactElement | undefined = undefined): ReactElement {
        if (isFolder) {
            return <FolderIcon/>;
        }
        if (UIUtils.isImage(extension ?? '')) {
            return <ImageIcon/>;
        }
        if (UIUtils.isVideo(extension ?? '')) {
            return <VideoIcon/>;
        }
        if (extension === 'pdf') {
            return <PdfIcon/>;
        }
        if (extension === 'csv') {
            return <TextIcon/>;
        }
        if (extension === 'zip') {
            return <ZipIcon/>;
        }
        return placeholder ?? <></>;
    }

    /**
     * Shows toasts associated with the status of the api response if the permission was granted in the request.
     *
     * @param request       The request that was executed.
     * @param response      The response that was received.
     */
    public static showToastApiCallEffect(request: any, response: any): void {

        const parseValidationError = () => {
            const data = response as any;
            let messageTitle = data?.headerTitle ?? Texts.serverErrorTitle;
            let message = data?.message ?? Texts.serverErrorMessage;
            // this is confluent errors. first check if the object has the errors and title fields
            if (data?.title) {
                messageTitle = data.title;
            }
            if (data?.errors && typeof data.errors === 'object') {
                message = '';
                for (const key of Object.keys(data.errors)) {
                    message += `
                            <li style="margin-left: -38px;">
                                <span class="text-sm secondary-color-1">${key}</span>
                                <ul>
                                    ${data.errors[key]
                        .map((err: string) =>
                            `<li class="text-xs" style="margin-left: -28px;">
                                        ${err}
                                    </li>`
                        ).join('')}
                                </ul>
                            </li>
                            `;
                }
                message = `<ul>${message}</ul>`;
            }
            return {
                message,
                title: messageTitle,
            }
        }

        import('../../core/services/toast').then(({default: ToastService}) => {
            if (request.showSuccessToast && response.resultFlag && !response.aborted) {
                ToastService.success({
                    title: response?.header ?? (response as any)?.headerTitle ?? Texts.serverSuccessTitle,
                    message: response?.message ?? Texts.serverSuccessMessage,
                });
            }
            if (!response.resultFlag && !response.aborted && request.showErrorToast) {
                const {message, title} = parseValidationError();
                ToastService.error({
                    title: title,
                    message: message,
                    hideTicketSubmission: (response?.code === 401 || (response as any)?.errorCode === 401),
                });
            }
        });
    }

    private static screenshotPromise: Promise<string | null> | undefined;
    private static lastScreenshot: { data: string | null; timestamp: number } | undefined;

    /**
     * Creates a screenshot of the user's browser window and sets it to the response.
     *
     * @param request       The request that was executed.
     * @param response      The response that was received.
     */
    static async captureScreenshotApiCallEffect(request: ApiRequestDs, response: ApiResponseDs): Promise<void> {
        if (response?.resultFlag || !EnvService.DEV_showErrorTicketSubmission) {
            return;
        }

        let capturedScreen: string | null = null;

        const now = Date.now();

        // If we have a recent screenshot (within 1 second), reuse it instead of capturing again
        if (this.lastScreenshot && (now - this.lastScreenshot.timestamp) < 2000) {
            capturedScreen = this.lastScreenshot.data;
        } else {
            if (!this.screenshotPromise) {
                this.screenshotPromise = Utils.screenshotUserBrowserWindow();
            }
            try {
                const data = await this.screenshotPromise;
                capturedScreen = data;
                this.lastScreenshot = {
                    data,
                    timestamp: Date.now(),
                };
            } catch {
                capturedScreen = null;
            } finally {
                this.screenshotPromise = undefined;
            }
        }

        response.viewScreenshot = capturedScreen;
    }

    /**
     * Converts a rgb string to a hex string.
     *
     * @param input
     * @param placeholderValue
     */
    static rgbStringToHex(input: any, placeholderValue: string = "#000000"): string {
        if (typeof input !== "string") return placeholderValue;

        // Check if input is already a valid hex color string
        // Matches: #RGB, #RRGGBB, #RGBA, #RRGGBBAA (case-insensitive)
        const hexPattern = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
        const trimmedInput = input.trim();
        if (hexPattern.test(trimmedInput)) {
            // Normalize to uppercase
            return trimmedInput.toUpperCase();
        }

        const s = trimmedInput.toLowerCase();
        const match = s.match(/^rgba?\s*\(\s*([^)]+)\s*\)$/i);
        if (!match) return placeholderValue;

        const body = match[1]
            .replace(/\s*,\s*/g, " ")   // commas → spaces
            .replace(/\s*\/\s*/g, " / ")// keep slash with spaces
            .trim();

        let parts = body.split(/\s+/);
        let alpha: string | null = null;

        const slashIdx = parts.indexOf("/");
        if (slashIdx !== -1) {
            alpha = parts.slice(slashIdx + 1).join(" ");
            parts = parts.slice(0, slashIdx);
        } else if (parts.length === 4) {
            alpha = parts.pop() ?? null;
        } else if (parts.length !== 3) {
            return placeholderValue;
        }

        const rgb: (number | null)[] = parts.map(v => {
            let n: number;
            if (/%$/.test(v)) {
                const p = parseFloat(v);
                if (Number.isNaN(p)) return null;
                n = Math.round((p / 100) * 255);
            } else {
                n = Math.round(parseFloat(v));
            }
            if (!Number.isFinite(n)) return null;
            return Math.max(0, Math.min(255, n));
        });

        if (rgb.includes(null) || rgb.length !== 3) return placeholderValue;

        let aByte: number | null = null;
        if (alpha !== null) {
            let aStr = alpha.trim();
            let aVal: number;
            if (/%$/.test(aStr)) {
                const p = parseFloat(aStr);
                if (Number.isNaN(p)) return placeholderValue;
                aVal = p / 100;
            } else {
                const num = parseFloat(aStr);
                if (Number.isNaN(num)) return placeholderValue;
                aVal = num > 1 ? num / 255 : num;
            }
            aVal = Math.max(0, Math.min(1, aVal));
            aByte = Math.round(aVal * 255);
        }

        const toHex2 = (n: number) => n.toString(16).padStart(2, "0").toUpperCase();
        const hex = `#${toHex2(rgb[0] as number)}${toHex2(rgb[1] as number)}${toHex2(rgb[2] as number)}`;
        return aByte === null ? hex : `${hex}${toHex2(aByte)}`;
    }

}

export * from './type-declerations';
export default UIUtils;
